import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { fetchUserPreferences } from '../lib/userPreferences';
import {
  baselineSeen,
  compareVersions,
  newestVersion,
  parseReleases,
  shownReleases,
  type ReleaseEntry,
} from '../utils/whatsNew';

/**
 * What's new — the release notes in the notification center (Phase 2).
 *
 * The notes come from `docs/RELEASES.md` inside this build (lazy `?raw`, so they
 * stay out of the entry chunk); the read state is ONE version per user,
 * `user_preferences.whats_new_seen` (mig 0285). Not persisted locally: it is
 * cheap to load and must follow the account, not the device.
 *
 * ⚠️ The marker only moves FORWARD, and only through `mark_whats_new_seen()` —
 * the RPC keeps the greater version, so a stale device can't un-read anything.
 */
interface WhatsNewState {
  /** Shown releases (subminor+), newest first. Empty until loaded. */
  releases: ReleaseEntry[];
  /** Newest version read; null until loaded. */
  seen: string | null;
  loadedFor: string | null;
  load: () => Promise<void>;
  /** Read this release and everything older. */
  markSeen: (version: string) => Promise<void>;
  markAllSeen: () => Promise<void>;
}

async function saveSeen(version: string): Promise<string | null> {
  const { data, error } = await supabase.rpc('mark_whats_new_seen', { p_version: version });
  if (error) {
    console.error("Error saving What's new read state:", error);
    return null;
  }
  return typeof data === 'string' ? data : null;
}

export const useWhatsNewStore = create<WhatsNewState>()((set, get) => ({
  releases: [],
  seen: null,
  loadedFor: null,

  load: async () => {
    try {
      const [{ default: markdown }, prefs, { user }] = await Promise.all([
        import('../../docs/RELEASES.md?raw'),
        fetchUserPreferences(),
        syncManager.safeAuthCheck(),
      ]);
      if (!user || !prefs) return;

      const releases = shownReleases(parseReleases(markdown));
      let seen = prefs.row?.whats_new_seen ?? null;
      if (seen === null) {
        // first load for this account: the owner's launch rule (see baselineSeen),
        // saved once so later releases stack up as unread from here
        seen = baselineSeen(releases, user.created_at ?? null);
        if (seen) {
          const stored = await saveSeen(seen);
          if (stored) seen = stored;
        }
      }
      set({ releases, seen, loadedFor: user.id });
    } catch (error) {
      console.error("Error loading What's new:", error);
    }
  },

  markSeen: async (version) => {
    const { seen } = get();
    if (seen !== null && compareVersions(version, seen) <= 0) return;
    set({ seen: version });
    const stored = await saveSeen(version);
    // the server keeps the greater of the two — adopt it if another device was ahead
    if (stored && compareVersions(stored, get().seen ?? '0.0.0.0.0') > 0) set({ seen: stored });
  },

  markAllSeen: async () => {
    const newest = newestVersion(get().releases);
    if (newest) await get().markSeen(newest);
  },
}));
